export const OPERATIONAL_CONTRACT_GOVERNANCE_VERSION =
  'v1-operational-contract-governance.2026-07-09';

export const OPERATIONAL_CONTRACTS_UPDATED_AT = '2026-07-09';

export const OPERATIONAL_CONTRACTS = Object.freeze({
  trendStructure: Object.freeze({
    label: 'Trend Structure',
    engineKey: 'v10',
    engineVersion: 'v10-pure-timeframe-pivot-trend-defended-reference-order.2026-07-08',
    primaryRangeStateVersion:
      'v10-pure-timeframe-pivot-trend-defended-reference-order.2026-07-08',
  }),
  opportunityRuntime: Object.freeze({
    label: 'Opportunity Runtime',
    ruleContractVersion:
      'v10-opportunity-runtime-price-action-defended-reference-order.2026-07-08',
  }),
  opportunityPriceAction: Object.freeze({
    label: 'Price Action - Oportunidades',
    ruleVersion: 'v1-opportunity-price-action.2026-07-08',
  }),
  opportunityUpdateGate: Object.freeze({
    label: 'Opportunity Update Gate',
    ruleVersion: 'v1-opportunity-update-gate.2026-07-09',
  }),
  entryZone: Object.freeze({
    label: 'Entry Zone',
    ruleVersion: 'v9-entry-zone-confluence-cluster-risk-boundary.2026-07-08',
  }),
  confluenceTemperature: Object.freeze({
    label: 'Confluencias / Temperatura',
    ruleVersion: 'v9-confluence-temperature.2026-07-06',
  }),
  fibonacci: Object.freeze({
    label: 'Fibonacci de Oportunidade',
    ruleVersion: 'v9-opportunity-fibonacci-retracement.2026-07-07',
  }),
  partialProfit: Object.freeze({
    label: 'Parciais',
    ruleVersion: 'v9-structure-rsi-ema-ladder.2026-07-06',
  }),
  entryZoneFailure: Object.freeze({
    label: 'Warning de Zona Confirmada',
    ruleVersion: 'v9-entry-zone-extreme-risk.2026-07-06',
  }),
  chartContext: Object.freeze({
    label: 'Chart Context',
    ruleVersion: 'v9-chart-context-contract.2026-07-06',
  }),
  stopMovement: Object.freeze({
    label: 'Stop Movement legado operacional controlado',
    legacyControlled: true,
    ruleVersion: 'v9-stop-main-structure.2026-07-06',
  }),
});

export const OPERATIONAL_CONTRACT_STATUSES = Object.freeze({
  blocked: 'blocked',
  legacyAllowed: 'legacy_allowed',
  missing: 'missing',
  official: 'official',
  outdated: 'outdated',
});

const SURFACE_FIELD_CANDIDATES = Object.freeze({
  chartContext: Object.freeze([
    ['chartContextRuleVersion'],
    ['ruleVersion'],
    ['chart_context', 'chartContextRuleVersion'],
    ['chartContext', 'chartContextRuleVersion'],
  ]),
  confluenceTemperature: Object.freeze([
    ['confluenceTemperatureRuleVersion'],
    ['ruleVersion'],
    ['analysisFacts', 'score', 'ruleVersion'],
    ['analysis_facts', 'score', 'ruleVersion'],
    ['confluenceContext', 'ruleVersion'],
    ['canonical_state', 'confluenceContext', 'ruleVersion'],
    ['canonicalState', 'confluenceContext', 'ruleVersion'],
  ]),
  entryZone: Object.freeze([
    ['entryZoneRuleVersion'],
    ['ruleVersion'],
    ['entryZone', 'ruleVersion'],
    ['canonical_state', 'entryZone', 'ruleVersion'],
    ['canonicalState', 'entryZone', 'ruleVersion'],
    ['detail_payload', 'entryZoneRuleVersion'],
    ['detailPayload', 'entryZoneRuleVersion'],
  ]),
  entryZoneFailure: Object.freeze([
    ['entryZoneFailureRuleVersion'],
    ['entryZoneWarningRuleVersion'],
    ['ruleVersion'],
  ]),
  fibonacci: Object.freeze([
    ['fibonacciRuleVersion'],
    ['ruleVersion'],
    ['fibonacciContext', 'ruleVersion'],
    ['canonical_state', 'fibonacciContext', 'ruleVersion'],
    ['canonicalState', 'fibonacciContext', 'ruleVersion'],
  ]),
  opportunityPriceAction: Object.freeze([
    ['priceActionRuleVersion'],
    ['ruleVersion'],
    ['priceActionContext', 'ruleVersion'],
    ['canonical_state', 'priceActionContext', 'ruleVersion'],
    ['canonicalState', 'priceActionContext', 'ruleVersion'],
  ]),
  opportunityRuntime: Object.freeze([
    ['ruleContractVersion'],
    ['canonical_state', 'ruleContractVersion'],
    ['canonicalState', 'ruleContractVersion'],
    ['detail_payload', 'ruleContractVersion'],
    ['detailPayload', 'ruleContractVersion'],
    ['ai_sanitized_context', 'ruleContractVersion'],
    ['aiSanitizedContext', 'ruleContractVersion'],
    ['chart_context', 'ruleContractVersion'],
    ['chartContext', 'ruleContractVersion'],
  ]),
  opportunityUpdateGate: Object.freeze([
    ['opportunityUpdateGateVersion'],
    ['updateGateVersion'],
    ['ruleVersion'],
    ['opportunityUpdateGate', 'version'],
    ['opportunityUpdateGate', 'ruleVersion'],
  ]),
  partialProfit: Object.freeze([
    ['partialProfitRuleVersion'],
    ['ruleVersion'],
    ['partialProfitContext', 'ruleVersion'],
  ]),
  stopMovement: Object.freeze([
    ['stopMovementRuleVersion'],
    ['ruleVersion'],
  ]),
  trendStructure: Object.freeze([
    ['engineVersion'],
    ['trendStructureEngineVersion'],
    ['primaryRangeStateVersion'],
    ['trendStructurePrimaryRangeStateVersion'],
    ['metadata', 'flow', 'primaryRange', 'stateVersion'],
    ['flow', 'primaryRange', 'stateVersion'],
    ['primaryRange', 'stateVersion'],
  ]),
});

function plainRecord(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function getPathValue(record, path) {
  let current = record;

  for (const key of path) {
    if (!current || typeof current !== 'object') {
      return undefined;
    }

    current = current[key];
  }

  return current;
}

function expectedVersionForSurface(surface, contract) {
  if (surface === 'trendStructure') {
    return contract.primaryRangeStateVersion ?? contract.engineVersion ?? null;
  }

  return contract.ruleContractVersion ?? contract.ruleVersion ?? null;
}

export function getOfficialContract(surface) {
  return OPERATIONAL_CONTRACTS[surface] ?? null;
}

export function readOperationalContractVersion(surface, payload) {
  const candidates = SURFACE_FIELD_CANDIDATES[surface] ?? [];
  const record = plainRecord(payload);

  for (const path of candidates) {
    const value = getPathValue(record, path);

    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

export function assertOfficialContract(surface, payload, options = {}) {
  const contract = getOfficialContract(surface);

  if (!contract) {
    return {
      actual: null,
      expected: null,
      official: false,
      status: OPERATIONAL_CONTRACT_STATUSES.blocked,
      surface,
    };
  }

  const expected = expectedVersionForSurface(surface, contract);
  const actual = readOperationalContractVersion(surface, payload);

  if (!actual) {
    const legacyAllowed = Boolean(options.allowLegacy && contract.legacyControlled);

    return {
      actual,
      expected,
      official: false,
      status: legacyAllowed
        ? OPERATIONAL_CONTRACT_STATUSES.legacyAllowed
        : OPERATIONAL_CONTRACT_STATUSES.missing,
      surface,
    };
  }

  const official = actual === expected;

  return {
    actual,
    expected,
    official,
    status: official
      ? OPERATIONAL_CONTRACT_STATUSES.official
      : OPERATIONAL_CONTRACT_STATUSES.outdated,
    surface,
  };
}

export function explainContractMismatch(surface, payload, options = {}) {
  const result = assertOfficialContract(surface, payload, options);

  if (result.official) {
    return `${surface}: versao oficial ${result.expected}`;
  }

  if (result.status === OPERATIONAL_CONTRACT_STATUSES.missing) {
    return `${surface}: contrato ausente; esperado ${result.expected}`;
  }

  if (result.status === OPERATIONAL_CONTRACT_STATUSES.legacyAllowed) {
    return `${surface}: legado controlado; esperado ${result.expected}`;
  }

  if (result.status === OPERATIONAL_CONTRACT_STATUSES.outdated) {
    return `${surface}: versao ${result.actual} desatualizada; esperado ${result.expected}`;
  }

  return `${surface}: contrato bloqueado`;
}

export function buildOfficialContractRefs() {
  return Object.freeze({
    chartContextRuleVersion: OPERATIONAL_CONTRACTS.chartContext.ruleVersion,
    confluenceTemperatureRuleVersion: OPERATIONAL_CONTRACTS.confluenceTemperature.ruleVersion,
    entryZoneFailureRuleVersion: OPERATIONAL_CONTRACTS.entryZoneFailure.ruleVersion,
    entryZoneRuleVersion: OPERATIONAL_CONTRACTS.entryZone.ruleVersion,
    fibonacciRuleVersion: OPERATIONAL_CONTRACTS.fibonacci.ruleVersion,
    operationalContractGovernanceVersion: OPERATIONAL_CONTRACT_GOVERNANCE_VERSION,
    partialProfitRuleVersion: OPERATIONAL_CONTRACTS.partialProfit.ruleVersion,
    priceActionRuleVersion: OPERATIONAL_CONTRACTS.opportunityPriceAction.ruleVersion,
    ruleContractVersion: OPERATIONAL_CONTRACTS.opportunityRuntime.ruleContractVersion,
    stopMovementRuleVersion: OPERATIONAL_CONTRACTS.stopMovement.ruleVersion,
    trendStructureEngineKey: OPERATIONAL_CONTRACTS.trendStructure.engineKey,
    trendStructureEngineVersion: OPERATIONAL_CONTRACTS.trendStructure.engineVersion,
    trendStructurePrimaryRangeStateVersion:
      OPERATIONAL_CONTRACTS.trendStructure.primaryRangeStateVersion,
    updateGateRuleVersion: OPERATIONAL_CONTRACTS.opportunityUpdateGate.ruleVersion,
  });
}
